import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowRight, Home as HomeIcon, Hammer, Leaf } from "lucide-react";
import { HeroSlider } from "@/components/HeroSlider";

const HERO_IMG =
  "https://customer-assets.emergentagent.com/job_444e252e-00e4-4168-9bd0-abf01b71d091/artifacts/tjb614io_iStock-2170674598.jpeg";
const CRAFTSMAN_IMG =
  "https://customer-assets.emergentagent.com/job_oberfranken-wohnen/artifacts/kuynkw9q_iStock-1273204850.jpg";
const PANORAMA_IMG =
  "https://customer-assets.emergentagent.com/job_oberfranken-wohnen/artifacts/ptemo1hn_iStock-1706562349.jpg";

const HERO_IMAGES = [
  {
    src: HERO_IMG,
    alt: "Sanierte Gründerzeit-Häuser in einer oberfränkischen Altstadtstraße",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_oberfranken-wohnen/artifacts/wrrujmym_iStock-2148796339.jpg",
    alt: "Belebte Fußgängerzone in der Innenstadt von Hof",
  },
  {
    src: "https://customer-assets.emergentagent.com/job_oberfranken-wohnen/artifacts/bmbx097j_iStock-648833060.jpg",
    alt: "Oberfränkische Kleinstadt mit Kirchturm und Wehr am Fluss",
  },
];

export default function Home() {
  return (
    <div data-testid="page-home">
      {/* Hero */}
      <section className="relative w-full min-h-[92vh] overflow-hidden">
        <HeroSlider images={HERO_IMAGES} />

        <div className="relative px-6 md:px-12 lg:px-24 pt-40 pb-24 min-h-[92vh] flex flex-col justify-center pointer-events-none">
          <div className="max-w-3xl fade-up hero-text">
            <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-white mb-6">
              <span className="inline-block w-2.5 h-2.5 bg-accent" />
              Immobilien in Oberfranken
            </div>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-normal tracking-tight text-white leading-[1.05]">
              Wir geben alten <br />
              Häusern eine <em className="italic font-normal">neue Zukunft</em>.
            </h1>
            <p className="mt-8 text-base sm:text-lg text-white max-w-xl leading-relaxed">
              Als inhabergeführtes Familienunternehmen kaufen, sanieren und
              verwalten wir kleinere Mehrfamilienhäuser in Hochfranken –
              zwischen Münchberg, Selb, Rehau und Hof.
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-4 pointer-events-auto">
              <Link to="/verkaufen" data-testid="hero-cta-sell">
                <Button className="rounded-none bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-8 text-sm tracking-wide">
                  Haus verkaufen <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/referenzen" data-testid="hero-cta-portfolio">
                <Button
                  variant="outline"
                  className="rounded-none bg-transparent border-white/70 text-white hover:bg-white hover:text-foreground h-12 px-8 text-sm tracking-wide"
                >
                  Portfolio ansehen
                </Button>
              </Link>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-6 md:left-12 lg:left-24 right-6 md:right-12 lg:right-24 flex justify-between items-end text-white/75 text-xs uppercase tracking-[0.25em] pointer-events-none">
          <span>Seit über 15 Jahren</span>
          <span className="hidden sm:block">Oberfranken · Nordbayern</span>
        </div>
      </section>

      {/* Philosophy split */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.25em] text-primary mb-6">Über uns</div>
            <h2 className="font-serif text-4xl sm:text-5xl font-normal tracking-tight leading-tight">
              Werte bewahren. <br />
              <em className="italic">Neues schaffen.</em>
            </h2>
            <p className="mt-8 text-base leading-relaxed text-muted-foreground">
              Wir verstehen die Verwaltung von Objekten als stolze Aufgabe. Dabei
              steht für uns ein faires, offenes und freundliches Miteinander mit
              unseren Mieterinnen und Mietern im Vordergrund.
            </p>
            <p className="mt-4 text-base leading-relaxed text-muted-foreground">
              Eine langfristige, möglichst störungsfreie Partnerschaft ist uns
              immer wichtiger, als kurzfristige Optimierungspotenziale voll
              auszuschöpfen.
            </p>
            <Link to="/ueber-uns" data-testid="home-cta-about">
              <Button
                variant="link"
                className="mt-8 px-0 text-primary rounded-none text-sm tracking-wide"
              >
                Unsere Philosophie kennenlernen <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
          <div className="lg:col-span-7 lg:col-start-7">
            <div className="overflow-hidden">
              <img
                src={CRAFTSMAN_IMG}
                alt="Steinerner Stadtbrunnen mit Geranien in einer oberfränkischen Altstadt"
                className="w-full h-auto transition-transform duration-700 hover:scale-105"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Hochfranken Impression — panorama */}
      <section className="relative w-full overflow-hidden">
        <div className="relative h-[60vh] min-h-[420px]">
          <img
            src={PANORAMA_IMG}
            alt="Panorama von Münchberg mit Kirchturm und bewaldeten Hügeln in Hochfranken"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/45 to-black/10" />
          <div className="relative h-full px-6 md:px-12 lg:px-24 flex items-end pb-16 md:pb-20">
            <div className="max-w-2xl text-white">
              <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-white mb-4 [text-shadow:_0_2px_10px_rgba(0,0,0,0.7)]">
                <span className="inline-block w-2 h-2 bg-accent" />
                Impressionen aus Hochfranken
              </div>
              <h3 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal leading-tight [text-shadow:_0_2px_18px_rgba(0,0,0,0.7)]">
                Eine Region zwischen Wald, <em className="italic">Hügeln und Handwerk</em>.
              </h3>
            </div>
          </div>
        </div>
      </section>

      {/* Three pillars */}
      <section className="bg-muted/40 border-y border-border">
        <div className="px-6 md:px-12 lg:px-24 py-20 md:py-24">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-12 md:gap-16">
            {[
              {
                icon: <HomeIcon className="h-6 w-6 text-primary" strokeWidth={1.5} />,
                title: "Wir kaufen",
                body: "Mehrfamilienhäuser bis sechs Einheiten mit historischem Charme – gern Gründerzeit oder Jugendstil – in Oberfranken und nördlichem Bayern.",
              },
              {
                icon: <Hammer className="h-6 w-6 text-primary" strokeWidth={1.5} />,
                title: "Wir sanieren",
                body: "Über mehrere Jahre, behutsam und kaufmännisch gekonnt. Energetisch optimierend, ohne den Charakter des Hauses zu verlieren.",
              },
              {
                icon: <Leaf className="h-6 w-6 text-primary" strokeWidth={1.5} />,
                title: "Wir verwalten",
                body: "Langfristig und in Eigenregie. Wir kennen unsere Mieter persönlich und stehen für eine partnerschaftliche Hausverwaltung.",
              },
            ].map((p, i) => (
              <div key={p.title} className={`fade-up fade-up-delay-${i}`}>
                <div className="mb-5">{p.icon}</div>
                <h3 className="font-serif text-2xl sm:text-3xl font-medium mb-3">{p.title}</h3>
                <p className="text-sm leading-relaxed text-muted-foreground">{p.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* What we look for */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.25em] text-primary mb-6">
              <span className="inline-block w-2 h-2 bg-accent" />
              Was wir suchen
            </div>
            <h2 className="font-serif text-4xl sm:text-5xl font-normal tracking-tight leading-tight">
              Häuser mit Geschichte. <em className="italic">Und Potenzial.</em>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <ul className="space-y-5">
              {[
                "Mehrfamilienhäuser bis 6 Einheiten",
                "Gründerzeit, Jugendstil oder ähnlicher Charme",
                "Standort in Oberfranken / Nordbayern",
                "Leichter bis vollständiger Sanierungsbedarf",
              ].map((item) => (
                <li key={item} className="flex items-start gap-4 text-base">
                  <span className="mt-2 block w-6 h-px bg-accent shrink-0" />
                  <span className="text-foreground">{item}</span>
                </li>
              ))}
            </ul>
            <Link to="/verkaufen" data-testid="home-cta-sell">
              <Button className="mt-10 rounded-none bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-8 text-sm tracking-wide">
                Ihr Haus anbieten <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Regional anchor */}
      <section className="bg-secondary text-secondary-foreground">
        <div className="px-6 md:px-12 lg:px-24 py-20 md:py-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-secondary-foreground/80 mb-6">
                <span className="inline-block w-2 h-2 bg-accent" />
                Regional verwurzelt
              </div>
              <h2 className="font-serif text-4xl sm:text-5xl font-light tracking-tight leading-tight">
                Zwischen Münchberg, Selb, <br />Rehau und Hof zu Hause.
              </h2>
              <p className="mt-6 text-base text-secondary-foreground/80 max-w-2xl leading-relaxed">
                Oberfranken ist nicht nur unser Schwerpunkt – wir sind
                persönlich mit der Region verbunden. Wir kennen die Städte, die
                Bausubstanz und die Menschen.
              </p>
            </div>
            <div className="lg:col-span-5 lg:pl-12">
              <div className="grid grid-cols-2 gap-6">
                {["Münchberg", "Selb", "Rehau", "Leupoldsgrün", "Hof", "Schwarzenbach / Saale"].map((city) => (
                  <div
                    key={city}
                    className="border border-secondary-foreground/20 px-4 py-5 flex items-center gap-3 hover:border-accent transition-colors duration-200"
                  >
                    <span className="inline-block w-1.5 h-1.5 bg-accent shrink-0" />
                    <span className="text-sm tracking-wide">{city}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 text-center">
        <div className="max-w-3xl mx-auto">
          <div className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-primary mb-6">
            <span className="inline-block w-2 h-2 bg-accent" />
            Ihr nächster Schritt
          </div>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-tight tracking-tight">
            Sie besitzen ein Haus, das eine <em className="italic">neue Geschichte</em> verdient?
          </h2>
          <p className="mt-6 text-base text-muted-foreground max-w-xl mx-auto">
            Wir prüfen Ihr Objekt diskret, persönlich und ohne Verpflichtung.
          </p>
          <Link to="/verkaufen" data-testid="home-final-cta">
            <Button className="mt-10 rounded-none bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-10 text-sm tracking-wide">
              Anfrage starten <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
